import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import type {
  CreateUserEmployeePayload,
  Employee,
  EmployeeListParams,
  EmployeeParams,
  HookParams,
  InviteUserPayload,
} from '@/libs/interfaces'
import { employeesApi } from '@/services/api'

export function useEmployeesInfinite({ params, enabled }: HookParams<EmployeeListParams>) {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.employees.infinite(params),
    queryFn: ({ pageParam }) =>
      employeesApi.listCursor({
        ...params,
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.pageInfo.next || undefined,
    getPreviousPageParam: (firstPage) => firstPage.pageInfo.prev || undefined,
    enabled: enabled ?? true,
    initialPageParam: undefined as string | undefined,
    staleTime: STALE_TIME['24Hours'], //24 Hours
  })
}

export function useEmployees({ params, enabled }: HookParams<EmployeeListParams>) {
  return useQuery<Partial<Employee[]>>({
    queryKey: QUERY_KEYS.employees.list(params),
    queryFn: () => employeesApi.list(params),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['24Hours'], //24 hours
  })
}

export function useEmployeesCreate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: employeesApi.create,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.employees.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useEmployeesByID({ params, enabled }: HookParams<EmployeeParams>) {
  return useQuery<Partial<Employee>>({
    queryKey: QUERY_KEYS.employees.id(params.employeeID),
    queryFn: () => employeesApi.getByID(params),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['24Hours'], //24 hours
  })
}

export function useEmployeesDelete() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: (params: { orgID?: string; employeeID?: string }) => employeesApi.delete(params),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employees.all })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}
export function useEmployeesFromUserID({
  params,
  enabled,
}: HookParams<{ orgID: string; userID: string }>) {
  return useQuery<Partial<Employee>>({
    queryKey: QUERY_KEYS.employees.fromUserID(params.orgID, params.userID),
    queryFn: () => employeesApi.getFromUserID(params.orgID, params.userID),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['24Hours'], //24 hours
  })
}

export function useInviteUser() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: (payload: InviteUserPayload) => employeesApi.inviteUser(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.employees.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useCreateUserEmployee() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({ orgID, payload }: { orgID: string; payload: CreateUserEmployeePayload }) =>
      employeesApi.createUserEmployee(orgID, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employees.all })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useEmployeesUpdate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: employeesApi.update,
    onSuccess: (_data) => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.employees.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}
